import styled, { css } from "styled-components";
import {
  ACCENT_PRIMARY,
  ERROR_LIGHT,
  ERROR_LIGHTEST,
  GRAY,
  GRAY_LIGHT,
  GRAY_LIGHTEST,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  WHITE
} from "@streeteasy/design-system/colors";
import { LARGE_SCREEN } from "@streeteasy/design-system/constants/screenSizes";
import {
  VisuallyHidden,
  visuallyHiddenCss
} from "@streeteasy/design-system/style-utilities/visually-hidden";
import { Body, Caption } from "@streeteasy/design-system/typography";

import { magnifyingGlass } from "../../assets";

const withDropdownCss = css`
  position: absolute;
  width: 100%;
  z-index: 1;
`;

export const textFieldStyles = css`
  -webkit-font-smoothing: antialiased;
  background-color: ${WHITE};
  border: 1px solid ${GRAY};
  color: ${TEXT_PRIMARY};
  font-family: "Source Sans Pro", Helvetica, Arial, Geneva, sans-serif;
  font-size: 14px;
  line-height: 22px;
  padding: 8px 15px;
  text-transform: none;
  width: 100%;

  &[aria-invalid="true"] {
    border-color: ${ERROR_LIGHT};
    background-color: ${ERROR_LIGHTEST};
  }

  &::placeholder {
    color: ${TEXT_SECONDARY};
  }

  &:focus {
    border-color: ${ACCENT_PRIMARY};
  }

  &:disabled {
    background-color: ${GRAY_LIGHT};

    &:not([value=""]) {
      background-color: ${GRAY_LIGHTEST};
    }
  }

  @media screen and (max-width: ${LARGE_SCREEN}px) {
    font-size: 16px;
    line-height: 24px;
  }
`;

const inactiveOptionCss = css`
  background: transparent;
  color: ${TEXT_PRIMARY};
`;

const activeOptionCss = css`
  background: ${GRAY_LIGHTEST};
  color: ${TEXT_PRIMARY};
`;

const embedOptionCss = ({ isActive }) =>
  isActive ? activeOptionCss : inactiveOptionCss;

export const ComboboxWrapper = styled.div`
  position: relative;
`;

export const Input = styled.input.attrs(({ isValid }) => ({
  "aria-invalid": !isValid,
  autoComplete: "off",
  type: "text"
}))`
  ${textFieldStyles};

  background-image: url(${magnifyingGlass});
  background-position: calc(100% - 12px) center;
  background-repeat: no-repeat;
  background-size: 12px;
  box-sizing: border-box;
  height: 40px;
  padding: 0 32px 0 16px;

  &:disabled {
    background-image: none;
  }
`;

export const InfoBlock = styled.div.attrs({
  "aria-live": "polite"
})`
  background: ${WHITE};
  border: 1px solid ${GRAY};
  box-shadow: 1px 2px 5px rgba(0, 0, 0, 0.05);
  font-size: 14px;
  font-weight: normal;
  line-height: 18px;
  padding: 16px;
  text-transform: none;

  ${withDropdownCss};

  ${({ isVisuallyHidden }) => isVisuallyHidden && visuallyHiddenCss}
`;

export const Datalist = styled.ul`
  background: ${WHITE};
  border: 1px solid ${GRAY};
  box-shadow: 1px 2px 5px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  display: ${({ isExpanded }) => (isExpanded ? "block" : "none")};
  margin: 0;
  max-height: 134px;
  overflow-y: auto;
  padding: 0;

  ${withDropdownCss};
`;

export const Option = styled(Body).attrs(({ isActive }) => ({
  "aria-selected": isActive,
  as: "li",
  role: "option"
}))`
  ${embedOptionCss};

  cursor: pointer;
  font-weight: normal;
  list-style: none;
  padding: 4px 16px;

  &:hover {
    ${activeOptionCss}
  }
`;

export const GroupLabel = styled(Caption).attrs({
  as: "li",
  role: "presentation"
})`
  color: ${TEXT_SECONDARY};
  list-style: none;
  font-weight: 400;
  margin-top: 6px;
  padding: 4px 16px;
`;

export const SearchStatus = styled(VisuallyHidden).attrs({
  as: "div"
})``;
